/**
 * NOS-133 M14: Per-event registrations list for org admins.
 */

import { Link, Navigate, useParams } from "react-router-dom"
import { Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { PermissionGate } from "@/components/PermissionGate"
import { useOrgStore } from "@/stores/orgStore"
import { formatEventDate } from "@/lib/format"
import { useEvent } from "@/hooks/useEvents"
import { useEventRegistrations } from "@/hooks/useEventRegistrations"
import { RegistrationsList } from "@/components/events/RegistrationsList"
import { toast } from "sonner"

export function EventRegistrationsPage() {
  const { eventId } = useParams<{ eventId: string }>()
  const { currentOrg } = useOrgStore()
  const orgId = currentOrg?.id ?? null

  const { event, loading: eventLoading } = useEvent(orgId, eventId ?? null)
  const {
    registrations,
    loading: regLoading,
    cancelRegistration,
    promoteFromWaitlist,
    exportCsv,
  } = useEventRegistrations(eventId ?? null, orgId)

  const confirmedHeadcount = registrations
    .filter((r) => r.status === "confirmed")
    .reduce((sum, r) => sum + (typeof r.headcount === "number" ? r.headcount : 0), 0)
  const capacity = event?.capacity

  const handleCancel = async (id: string) => {
    const ok = await cancelRegistration(id)
    if (ok) toast.success("Registration cancelled")
    else toast.error("Could not cancel registration")
  }

  const handlePromote = async (id: string) => {
    const ok = await promoteFromWaitlist(id)
    if (ok) toast.success("Promoted from waitlist")
    else toast.error("Could not promote registration")
  }

  if (!eventId) {
    return <Navigate to="/dashboard/events" replace />
  }

  return (
    <PermissionGate permission="can_manage_events" fallback={<Navigate to="/dashboard" replace />}>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/dashboard/events">← Events</Link>
          </Button>
          {event && (
            <Button variant="ghost" size="sm" asChild>
              <Link to={`/dashboard/events/${event.id}/edit`}>Edit event</Link>
            </Button>
          )}
        </div>

        {eventLoading || !event ? (
          <div className="flex min-h-[240px] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div>
              <h1 className="text-2xl font-bold">{event.title}</h1>
              <p className="mt-1 text-muted-foreground">
                {formatEventDate(event.start_date, event.end_date)}
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                {capacity != null ? (
                  <>
                    <span className="font-medium text-foreground">{confirmedHeadcount}</span>
                    {" / "}
                    <span className="font-medium text-foreground">{capacity}</span>
                    {" confirmed"}
                  </>
                ) : (
                  <>
                    <span className="font-medium text-foreground">{confirmedHeadcount}</span>
                    {" confirmed"}
                  </>
                )}
              </p>
            </div>

            {regLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <RegistrationsList
                registrations={registrations}
                onCancel={(id) => void handleCancel(id)}
                onPromote={(id) => void handlePromote(id)}
                onExportCsv={() => {
                  exportCsv(registrations, event.slug)
                  toast.success("CSV downloaded")
                }}
              />
            )}
          </>
        )}
      </div>
    </PermissionGate>
  )
}
